import type { TemplateOption } from 'candidate_profile_redesign/shared/types';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { SCORECARD_STATUS } from 'talent_search/shared/types';
import type AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import { FraudReport } from 'fraud/types';
export type Question = {
  question: string;
  question_label: string;
  question_description?: string;
  answer: string;
  hidden?: boolean;
  value_type?: string;
};

export interface Geolocation {
  city: string;
  displayName: string;
  latitude: string;
  longitude: string;
  location: string;
  label: string;
  value: string;
}

export type CompanyMode = 'include' | 'exclude';
export type CompanyNameFilter = {
  id: string;
  mode: CompanyMode;
  value: string;
};

export type MatchStrength =
  | 'unknown'
  | 'limited_match'
  | 'partial_match'
  | 'good_match'
  | 'strong_match';

export type MatchReportSkill = {
  skill_name?: string | null;
  skill_cluster?: string | null;
  rank?: number | null;
  assessment_category?: string | null;
  attested_job?: boolean | null;
  attested_candidate?: boolean | null;
  candidate_strength_score?: number | null;
  job_importance_score?: number | null;
};

export type MatchReport = {
  attributes?: {
    skills_category?: string | null;
    skills_score?: number | null;
    work_history_category?: string | null;
    work_history_score?: number | null;
    education_category?: string | null;
    education_score?: number | null;
  } | null;
  education?: {
    job_degree_min?: string | null;
    candidate_degree_max?: string | null;
    meets_requirement?: string | null;
  } | null;
  skills?: {
    technical?: MatchReportSkill[] | null;
    baseline?: MatchReportSkill[] | null;
  } | null;
};

type MatchStrengthExplanationType = {
  experience_explanation: string | null;
  skills_explanation: string | null;
  industry_explanation: string | null;
  unknown: boolean;
  no_matched_criteria: boolean;
};

export type Scorecard = {
  author: string;
  body: string;
  candidate_rating_id: number;
  submitted_at: string;
  decision: { key: string; label: string };
  id: number;
  is_current_user?: boolean;
};

export type ResumeEmployment = {
  title: string;
  employer: string | null;
  start_date: string | null;
  end_date: string | null;
};

type ResumeDetails = {
  address: string | null;
  employments: ResumeEmployment[];
};

export type Document = {
  id?: number;
  downloadUrl: string;
  extension: string;
  display_name?: string;
  file_name: string;
  type: string;
  virus_status?: string;
  visibility?: string;
  previewUrl?: string; // standard resume
  preview_path?: string | null; // anonymized resume
  previewable?: boolean;
};

export type SidePanelDetails = {
  personId: number;
  advancePath: string | null;
  hasOtherApplications: boolean;
  applicationHistoryPath: string;
  applicationQuestions: Question[];
  agencyQuestions: Question[];
  referralDetails: Question[];
  lastSeenFraudReport: string | null;
  attachments?: Document[];
  paths: {
    documentsPath: string;
    fraudReportPath: string;
    updateLastSeenFraudReportPath: string;
    restoreFraudReportPath?: string;
    resolveFraudReportPath?: string;
  };
};

export type Row = {
  id: number;
};

export type ResumeParsingStatus = 'in_progress' | 'complete' | 'failed';

export type BaseResume = {
  url: string;
  type: 'docx' | 'pdf' | 'txt' | 'rtf';
  document: Document;
};

export type BaseCandidate = {
  id: number;
  fullName: string;
  tags: Array<{ label: string; value: string | number }>;
  linkedinAddress: string | null;
  githubAddress?: string | null;
  websiteAddress?: string | null;
  referral: boolean;
  internal: boolean;
  activeOnAnotherJob?: boolean;
  activeJobSeeker?: boolean;
  recentlyActive?: boolean;
  potentialDuplicate?: boolean;
  resume?: BaseResume | null;
};

export type MatchedSkill = {
  name: string | null;
  importance: number;
  exact: boolean;
  base_skill: string;
  section?: 'technical' | 'baseline' | string | null;
  assessmentCategory?: string | null;
  rank?: number | null;
};

export type ApplicationScore = {
  matchScore: number;
  matchedSkills: MatchedSkill[];
  numCalibrationSkills: number;
  matchStrength: MatchStrength | null;
  matchStrengthExplanation: MatchStrengthExplanationType;
  overrideMatchScorePath?: string;
  overrideMatchStrength?: MatchStrength | null;
  overrideNote?: string | null;
  overrideReason?: string | null;
};

export type PersonTags = {
  tags: Array<{ label: string; value: number }>;
};

export type Person = BaseCandidate & {
  appliedOn?: string;
  sourceName?: string | null;
  company: string;
  title: string;
  profileLocation: string | null;
  latestEmployment: {
    company_name: string;
    title: string;
    start_date: string;
    end_date: string | null;
  } | null;
  hiringPlanId?: number;
  currentHiringPlanInterviewStage: { name: string; id: number } | null;
  nextHiringPlanInterviewStage: { name: string; id: number } | null;
  linkedinIframePath: string | null;
  resume?:
    | (BaseResume & {
        details: ResumeDetails;
        parsing_status?: ResumeParsingStatus;
      })
    | null;
  fromAgency: boolean;
  previouslyRejectedFromJob: boolean;
  dreamJob: boolean;
  submitFeedbackPath: string;
  jobPost: { title: string } | null;
  rejectPath: string;
  rejectModalPath: string;
  addTagsPath: string;
  tags: Item[];
  isBookmarked: boolean;
  scorecards?: Scorecard[];
  userScorecard?: Scorecard;
  emailCandidatePath: string;
  anonymizeResumes: boolean;
  candidateProfileContextPath: string;
  transferModalHiringPlansPath: string;
  transferModalOwnerOptionsPath: string;
  email: string | null;
  addPersonToJobPath: string;
  fraudReportHighRisk: boolean;
  fraudReport?: FraudReport;
  decision?: { status: string };
} & Partial<SidePanelDetails> &
  Partial<ApplicationScore>;

export type RediscoveryAction = {
  id: number;
  action:
    | 'added_as_prospect'
    | 'tags_amended'
    | 'sent_email'
    | 'added_to_campaign'
    | 'added_to_this_job'
    | 'added_to_another_job';
  metadata: Record<string, unknown> | null;
  organization_id: number;
  hiring_plan_id: number;
  application_id: number;
  user_id: number;
  user_name: string | null;
  created_at: string;
  updated_at: string;
};

export type RediscoveryApplication = BaseCandidate & {
  appliedOn: string;
  hiringPlanId: number;
  hiringPlanName: string;
  currentStage: { id: number; name: string } | null;
  rejectedOn: string | null;
  rejectionReason: string | null;
  resume?: (BaseResume & { parsing_status: string }) | null;
  candidateProfilePath: string;
  activeAppsCount: number;
  pastAppsCount: number;
  milestone: string | null;
  positiveRatingPercentage: number | null;
  rediscoveryActions: RediscoveryAction[];
  addTagsPath: string;
  makeProspectPath: string;
  sourceAndResponsibilityPath: string;
  candidateProfileContextPath: string;
  emailCandidatePath: string;
  addPersonToJobModalPath: string;
  addPersonToJobPath: string;
};

export type TalentMatchingApplication = Partial<Person> & {
  id: number;
  hiringPlanId: number;
  fullName: string;
};

export type ScorecardRatingsSummary = {
  strong_no: number;
  no: number;
  yes: number;
  strong_yes: number;
  no_decision: number;
};

export type ScorecardDetail = {
  id: number;
  interviewerName: string | null;
  interviewName: string | null;
  rating: string;
  scorecardUrl: string;
};

export type RediscoveryApplicationHistoryItem = {
  id: number;
  hiringPlanName: string;
  url: string;
  hiringPlanUrl: string;
  requisitionId: string | null;
  recruiter: string | null;
  applicationStageName: string | null;
  daysInStage: number | null;
  appliedDate: string;
  source: string | null;
  rejectedOn: string | null;
  rejectionReason: string | null;
  rejectionNote: string | null;
  hiredOn: string | null;
  scorecardRatingsSummary: ScorecardRatingsSummary;
  scorecardDetails: ScorecardDetail[];
  scorecardSummary: string | null;
  positiveRatingPercentage: number | null;
  attachments: Document[];
  matchingApplication?: boolean;
};

type AiScorecardSummaryInfo = {
  overallSentiment: string | null;
  stale: boolean;
  taskId: number | null;
  startSummaryPath: string;
  checkSummaryStatusPath: string;
};

type RediscoveryCurrentApplication = RediscoveryApplicationHistoryItem & {
  milestone: string | null;
  rejectionReason: string | null;
  aiScorecardSummary: AiScorecardSummaryInfo | null;
};

export type RediscoveryApplicationHistory = {
  currentApplication: RediscoveryCurrentApplication;
  allApplicationsCount: number;
  activeApplications: RediscoveryApplicationHistoryItem[];
  previousApplications: RediscoveryApplicationHistoryItem[];
  candidateProfileUrl: string;
};

// eslint-disable-next-line no-shadow
export enum AppliedOnType {
  YESTERDAY = 'yesterday',
  TODAY = 'today',
  LAST_SEVEN_DAYS = 'last_seven_days',
  LAST_6_MONTHS = 'last_6_months',
  LAST_12_MONTHS = 'last_12_months',
  LAST_2_YEARS = 'last_2_years',
  BETWEEN = 'between',
  ON_OR_AFTER = 'on_or_after',
  ON_OR_BEFORE = 'on_or_before',
}

export interface FilterType extends Item<string> {
  operators: Item[];
  valueType:
    | 'short_text'
    | 'long_text'
    | 'yes_no'
    | 'single_select'
    | 'multi_select'
    | 'currency'
    | 'number'
    | 'date'
    | 'user';
  valuesPath?: string;
  values?: Item[];
}

export type AdvancedFilterType = {
  type: FilterType | null;
  operator: Item | null;
  value: Item | Item[] | string | null;
  reactKey?: string;
};

export interface TalentMatchingFilters {
  page: number;
  perPage: number;
  appliedOnEnd: string | null;
  appliedOnStart: string | null;
  appliedOnType: AppliedOnType | null;
  jobPosts: Item[];
  degrees: Array<string>;
  disciplines: Array<string>;
  excludedIds: number[];
  feedbackStatus: SCORECARD_STATUS;
  hideRejected: boolean;
  jobAlertSubscribers: boolean;
  locationFilterType: 'name' | 'radius';
  locationName: string | null;
  debouncedLocationName: string | null;
  geolocation: Geolocation | null;
  geolocationRadius: string;
  manualReview: boolean;
  priorityCandidates: boolean;
  bookmarkedByTeammates: Item[];
  savedForLater: 'saved' | 'not_saved' | null;
  schools: Array<string>;
  tags: Array<string>;
  canHave: Array<string>;
  mustHave: Array<string>;
  doesNotHave: Array<string>;
  excludePreviouslyRejected: boolean;
  excludeHighRisk: boolean;
  otherActiveApplications: '1' | '0' | null;
  minExperience: string | null;
  maxExperience: string | null;
  companyNameFilters: CompanyNameFilter[];
  educationFilters: AdvancedFilter[];
  advancedFilters: AdvancedFilter[];
  questionFilters: AdvancedFilter[];
}

interface CustomFieldParams {
  [key: `cf_${string}`]: Array<string>;
  [key: `txt_cf_${string}`]: Array<string>;
  [key: `defined_cf_${string}`]: '1' | '0';
}

interface UnlinkedQuestionParams {
  [key: `unlinked_q_${string}`]: Array<string>;
  [key: `txt_unlinked_q_${string}`]: Array<string>;
  [key: `defined_unlinked_q_${string}`]: '1' | '0';
}

export type AdvancedFilterParams = CustomFieldParams &
  UnlinkedQuestionParams & {
    any_candidate_tag_id?: string[];
    candidate_tag_id?: string[];
    has_cover_letter?: string[];
    source_id?: string[];
    without_source_id?: string[];
    application_question?: string[];
    agency_question?: string[];
    referral_question?: string[];
    degree_contains?: string[];
    degree_id?: string[];
    discipline_contains?: string[];
    discipline_id?: string[];
    school_name_contains?: string[];
    school_name_id?: string[];
  };

interface FilterParams {
  application_created_at: string | undefined | null;
  application_created_end: string | undefined | null;
  job_post_id?: string[];
  exclude_application_ids: number[];
  exclude_previously_rejected: boolean;
  geolocation?: Geolocation;
  geolocation_radius?: string;
  manual_review: boolean;
  priority_candidates: boolean;
  without_potential_fraud: boolean;
  bookmarked_by_user_id?: string[];
  saved_for_later: 'saved' | 'not_saved' | null;
  other_active_applications?: '1' | '0' | null;
  scorecard_status: SCORECARD_STATUS;
  years_of_experience_min: string | null;
  years_of_experience_max: string | null;
  can_have?: string[];
  must_have?: string[];
  does_not_have?: string[];
}

export interface TalentMatchingQueryParams extends AdvancedFilterParams {
  queryParams: {
    page: number;
    page_size: number;
  };
  filterParams: FilterParams;
}

export interface Paths {
  allJobsPath: string;
  jobDashboardPath: string;
  bulkRejectionModalPath: string;
  advanceStagePath: string | null;
  topSkillsPath: string;
  calibrationSettingsPath: string;
  calibrationHistoryPath: string;
  peoplePath: string;
  referralQuestionOptionsPath: string;
  applicationQuestionOptionsPath: string;
  agencyQuestionOptionsPath: string;
  emailModalPath: string;
  bulkAddTagsPath: string;
  bulkFeedbackPath: string;
  bulkRequestFraudReportsPath: string;
  hiringPlanTagsPath: string;
  exportScoresPath: string;
  rankingInProgressPath: string;
  indexingStatusPath: string;
  jobPostOptionsPath: string;
  generateSkillsDescriptionPath: string;
  generateCalibrationFromTextPath: string;
  configurationAssistantChatsPath: string;
  appReviewPath: string;
  savedSearchesPath: string;
  saveAllCalibrationsPath: string;
  saveAndEmailScoresPath: string;
  industriesPath: string;
  calibrationSuggestionsPath: string;
}

export type CalibrationSuggestion = {
  name: string;
  current_importance: number;
  suggested_importance: number;
};

export type WeightOptimizationResult = {
  success: boolean;
  optimized_weights: number[];
  initial_weights: number[];
  skills: string[];
  metrics: {
    optimization_metric_value: number;
    baseline_metrics: Record<string, number>;
    optimized_metrics: Record<string, number>;
  };
  error?: boolean;
  message?: string;
};

export type Skill = {
  name: string;
  importance: number;
  newManuallyAdded?: boolean;
  isPipelineSkill?: boolean;
};

export type SkillValidationError = {
  skill: string;
  category:
    | 'subjective_unmeasurable'
    | 'explicit_protected'
    | 'implicit_bias'
    | 'unrecognized'
    | 'errors';
  severity: 'warn' | 'block';
  reason: string;
};

export interface CalibrationSettings {
  skills: Skill[];
  job_titles: string[];
  use_boolean_search: boolean;
  min_years_experience?: number | '';
  dev_only_all_apps?: boolean;
  proceed_despite_warnings?: boolean;
  warned_skill_names?: string[];
  prioritize_current_experience?: boolean;
  extracted_skills?: string[];
  industries?: string[];
  userPrompt?: string;
  include_job_description?: boolean;
  include_scorecard_attributes?: boolean;
  suggested_prompt?: string | null;
  updated_by_id?: number | null;
  updated_by_name?: string | null;
}

type ConfigurationAssistantAdvancedFilterSuggestion = {
  category: string;
  label: string;
  filter_key: string;
  operator: string;
  value: string;
};

export type ConfigurationAssistantFilters = {
  location_name: string;
  application_created_at: string;
  application_created_end: string;
  min_years_experience: number | null;
  max_years_experience: number | null;
  company_name_includes: string[];
  company_name_excludes: string[];
  must_have: string[];
  can_have: string[];
  does_not_have: string[];
  priority_candidates: boolean;
  manual_review: boolean;
  exclude_previously_rejected: boolean;
  exclude_high_risk: boolean;
  saved_for_later: boolean;
  other_active_applications: '' | 'include' | 'exclude';
  feedback_status:
    | ''
    | SCORECARD_STATUS.COMPLETED
    | SCORECARD_STATUS.INCOMPLETE;
  advanced_filter_suggestions: ConfigurationAssistantAdvancedFilterSuggestion[];
  advanced_filters: AdvancedFilterType[];
  question_filters: AdvancedFilterType[];
  education_filters: AdvancedFilterType[];
};

export type ConfigurationAssistantProposal = {
  filters: ConfigurationAssistantFilters;
  calibration_settings: CalibrationSettings;
};

export type SettingsFormData = {
  skills: Skill[];
  job_titles: string[];
  use_boolean_search: boolean;
  min_years_experience?: number | '';
  dev_only_all_apps?: boolean;
  prioritize_current_experience?: boolean;
  include_job_description?: boolean;
  include_scorecard_attributes?: boolean;
  userPrompt?: string;
  jobPostSkills?: Skill[];
  scorecardSkills?: Skill[];
  pipelineSkills?: Skill[];
  userPromptMode?: 'initial' | 'view' | 'edit';
  extracted_skills?: string[];
  industries?: string[];
};

export type CalibrationHistoryEntry = CalibrationSettings & {
  id: number;
  created_at?: string;
  updated_at?: string;
  has_criteria?: boolean;
  formatted_created_at?: string | null;
  is_active?: boolean;
};

export type CalibrationHistoryResponse = {
  history: CalibrationHistoryEntry[];
};

export type CalibrationSettingsResponse = {
  settings: CalibrationSettings | null;
  has_job_description: boolean;
  has_scorecard_attributes: boolean;
  suggested_prompt?: string | null;
};

export type SendEmailModalOptionsResponse = {
  defaultTemplate: TemplateOption | null;
  templateOptions: TemplateOption[];
  fromOptions: Item[];
  getCcOptionsPath: string;
  bulkEmailCandidatesPath: string;
};

export type FilterGroup = Item & {
  items: FilterType[];
};

export type QuestionFilterConfig = {
  filterGroups: FilterGroup[];
};

export type EducationFilterConfig = {
  filterGroups: FilterGroup[];
};

export type AdvancedFilterConfig = {
  filterGroups: FilterGroup[];
  currencies: Item[];
};

export type RankingDisabledReason = {
  text: string;
  link_text: string;
  path: string;
};

export type OverrideMatchScoreFormData = {
  match_strength: Item | null;
  note: string;
  reason: string | null;
};

export type { Props as RediscoveryProps } from 'talent_search/talent_rediscovery/container';

export type MyGreenhousePaths = {
  candidatesPath: string;
  savedSearchesPath: string;
  locationAutocompletePath: string;
  matchNarrativePath?: string;
};

type NarrativeSection = {
  heading: string;
  body: string;
};

export type MatchNarrative = {
  summary: string | null;
  sections: NarrativeSection[];
};

export type MyGreenhouseProps = {
  paths: MyGreenhousePaths;
};
